import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';

// documentation available at https://docs.talkdesk.com/docs/talkdesk-embedded-for-custom-integrations-customer-facing
// when testing locally, use https://chromewebstore.google.com/detail/ignore-x-frame-headers/gleekbfjekiniecknbkamfmkohkpodhe to ignore the Talkdesk CSP
@Component({
  selector: 'ss-stx-shared-talkdesk-cti',
  templateUrl: './talkdesk-cti.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:message)': 'onMessage($event)',
  },
})
export class TalkdeskCtiComponent {
  public readonly agentId = input.required<string>();
  public readonly event = output<TalkdeskCtiEventPayload>();

  private readonly frame =
    viewChild.required<ElementRef<HTMLIFrameElement>>('ctiFrame');
  private readonly document = inject(DOCUMENT);

  protected onMessage(event: MessageEvent<TalkdeskCtiEventPayload>) {
    const frame = this.frame();

    if (event.origin !== new URL(frame.nativeElement.src).origin) {
      return;
    }

    this.event.emit(event.data);
  }

  protected onFrameLoaded() {
    this.sendMessage({
      action: 'init_td_embedded',
      data: {
        url: this.document.location.origin,
        int: 'showtix',
        env: 'prd-us',
        aid: this.agentId(),
        view: 'external',
      },
    });
  }

  private sendMessage(payload: TalkdeskCtiActionPayload) {
    const frame = this.frame();

    frame.nativeElement.contentWindow?.postMessage(
      payload,
      frame.nativeElement.src
    );
  }
}

// https://docs.talkdesk.com/docs/talkdesk-embedded-for-custom-integrations-customer-facing#event-handler-messages-published-by-the-talkdesk-embedded

type TalkdeskCtiAgentStatus =
  | 'available'
  | 'away'
  | 'offline'
  | 'on a call'
  | string;

export type TalkdeskCtiActionPayload =
  | {
      action: 'init_td_embedded';
      data: {
        url: string;
        int: string;
        env: 'prd-us' | 'prd-ca' | 'prd-eu' | 'prd-au';
        aid: string;
        view: 'external' | 'workspace' | 'headless';
        app?: 'conversation' | 'agent-assist' | 'live' | 'canvas-embedded';
        show_cti_status?: boolean;
        renderMode?: 'host' | 'renderer';
      };
    }
  | {
      action: 'clickToCall';
      data: {
        to: string;
        from: string;
        contactId?: string;
        metadata?: Record<string, string>;
      };
    }
  | {
      action: 'integrationStatusChanged';
      data: {
        status: TalkdeskCtiAgentStatus;
      };
    };

export type TalkdeskCtiEventPayload =
  | {
      action: 'login';
      data: undefined;
    }
  | {
      action: 'logout';
      data: undefined;
    }
  | {
      action: 'userLogoutForced';
      data: undefined;
    }
  | {
      action: 'hide';
      data: undefined;
    }
  | {
      action: 'show';
      data: undefined;
    }
  | {
      action: 'popover';
      data: undefined;
    }
  | {
      action: 'openContact';
      data: {
        externalId: string;
      };
    }
  | {
      action: 'sendData';
      data: Record<string, string>;
    }
  | {
      action: 'agentStatusChanged';
      data: {
        status: TalkdeskCtiAgentStatus;
      };
    };
